/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
// View-only exercise detail for the calendar sidebar "phone" preview — a faithful copy of
// the athlete's mobile workout card (workouts module CarouselCard). It renders the full UI
// for every exercise type; a transparent overlay over the body blocks all interaction.
// The header video is the one exception — it plays via the calendar's own videoModal.
import React from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import Linkify from 'react-linkify';
import { useTheme } from 'emotion-theming';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { BoxShadow } from '../../../../shared/GlobalStyles';
import SubText from '../../../../shared/components/SubText/SubText';
import { setActiveModal } from '../../ducks/calendarActions';

import CalendarEntryTable from './CalendarEntryTable';
import CalendarWorkoutNotes from './CalendarWorkoutNotes';
import CalendarSessionBreakCard from './CalendarSessionBreakCard';
import CalendarVideoCard from './CalendarVideoCard';
import CalendarHealthCard from './CalendarHealthCard';
import CalendarProgressionCard from './CalendarProgressionCard';
import CalendarTimerCard from './CalendarTimerCard';
import CalendarTagCard from './CalendarTagCard';
import CalendarQuestionnaire from './CalendarQuestionnaire';
import CalendarWorkoutDocuments from './CalendarWorkoutDocuments';

const Wrapper = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: 'Nunito Sans';
  background: #f8f8f8;
  border-radius: 12px;
  overflow: hidden;
  flex: 1;
`;

const UpperBox = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 15px;
  position: relative;
  min-height: 160px;
  background: #f8f8f8;
  cursor: ${(props) => (props.isVideo ? 'pointer' : 'default')};
`;

const VideoContainer = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const VideoOverlay = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${(props) => (props.videoTrue ? 'rgba(0, 0, 0, 0.7)' : 'none')};
`;

const Video = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
  background-image: ${(props) => (props.background ? `url(${props.background})` : 'none')};
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;

const UpperBoxBottom = styled('div')`
  justify-content: space-around;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  z-index: 1;
  height: 100%;
  width: 90%;
  padding-top: 15px;
  padding-bottom: 15px;
`;

const NameAndPlay = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PlayButton = styled('div')`
  width: 33px;
  height: 33px;
  border-radius: 50%;
  border: 2px solid white;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    margin-left: 2px;
    margin-top: 1px;
  }
`;

const ExerciseName = styled('div')`
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  color: ${(props) => (props.videoTrue ? 'white' : 'black')};
  margin-top: ${(props) => (!props.videoTrue ? '20px' : '5px')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  text-align: center;
`;

const MaxTempBox = styled('div')`
  display: flex;
  justify-content: space-between;
  color: ${(props) => (props.videoTrue ? 'white' : 'grey')};
  width: 100%;
  font-size: 12px;
`;

const IconAndCardLine = styled('div')``;

const IconContainer = styled('div')`
  display: flex;
  position: absolute;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: ${(props) => (!props.completed ? `1px solid ${props.borderColor}` : '#0dcc8a')};
  height: 25px;
  width: 25px;
  background: ${(props) => (props.completed ? '#0dcc8a' : 'white')};
  bottom: -10px;
  left: 30px;
  transform: translateX(-50%);
  z-index: 1;
`;

const GroupingLetterContainer = styled('div')`
  display: flex;
  align-items: center;
  border-radius: 50px;
  border-width: 1px;
  height: 12px;
  justify-content: center;
  padding: 1.5px;
  position: absolute;
  width: 12px;
  border: ${(props) => (!props.completed ? `1px solid ${props.borderColor}` : '#0dcc8a')};
  background: ${(props) => props.borderColor};
  right: -4px;
  top: -4px;
`;

const GroupingLetter = styled('div')`
  color: ${(props) => props.color};
  font-size: 9px;
  font-weight: 500;
`;

const CardLine = styled('div')`
  display: flex;
  position: absolute;
  height: 3px;
  width: 100%;
  background: linear-gradient(#ff9a44, #fc6076);
  bottom: 0;
  left: 0;
`;

const GreenCardLine = styled('div')`
  display: flex;
  position: absolute;
  height: 3px;
  width: 100%;
  background: #0dcc8a;
  bottom: 0;
  left: 0;
`;

const LowerBox = styled('div')`
  display: flex;
  width: 100%;
  padding: 15px;
  flex-direction: column;
  flex: 1;
  margin-bottom: 15px;
  overflow: auto;
  position: relative;
`;

// Transparent layer over the body: blocks all clicks/typing so the preview is non-interactive.
const Overlay = styled('div')`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100;
  background: transparent;
`;

const InteriorWrap = styled('div')`
  display: flex;
  flex-direction: column;
`;

const MiddleButtons = styled('div')`
  display: flex;
  width: 100%;
  min-height: 33px;
  justify-content: space-evenly;
  margin-bottom: 15px;
`;

const MiddleButton = styled('div')`
  display: flex;
  border: none;
  background: white;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  flex: 3;
  margin-left: 5px;
  margin-right: 5px;
  padding: 6px;
  box-shadow: ${BoxShadow};
  position: relative;
`;

const IconTextWrapper = styled('div')`
  display: flex;
  align-items: center;
  svg {
    margin-right: 5px;
  }
`;

const MediaCount = styled('div')`
  position: absolute;
  width: 35px;
  height: 20px;
  right: 0;
  top: 0;
  border-radius: 25px;
  margin-top: -10px;
  margin-right: -8px;
  background: #b8e8f9;
  font-size: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-weight: bold;
  z-index: 1;
`;

const OptOutWrapper = styled('div')`
  display: flex;
  border: none;
  background: white;
  align-items: center;
  border-radius: 12px;
  width: 100%;
  flex-direction: column;
  box-shadow: ${BoxShadow};
  padding: 5px;
  white-space: normal;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const OptOutText = styled('div')`
  color: red;
  font-size: 10px;
`;

const AdditionalInformation = styled('div')`
  display: flex;
  border: none;
  background: white;
  justify-content: center;
  border-radius: 12px;
  width: 100%;
  align-items: center;
  box-shadow: ${BoxShadow};
  padding: 10px;
  white-space: normal;
  margin-bottom: 15px;
  margin-top: 15px;
`;

const AdditionalInformationText = styled('div')`
  display: flex;
  font-size: 12px;
  font-weight: 400;
  flex-direction: column;
  font-family: 'Nunito Sans';
  text-align: center;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  a {
    font-size: 12px;
    font-weight: 400;
    font-family: 'Nunito Sans';
  }
`;

const showButtons = (object) => !(
  object.formType === 'TextOnly' || object.type === 'RT' || object.type === 'V'
);

const linkDecorator = (decoratedHref, decoratedText, key) => (
  <a target='blank' href={decoratedHref} key={key}>{decoratedText}</a>
);

const CalendarExerciseDetail = ({ workoutObject }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const media = workoutObject?.exercise?.media;
  const videoTrue = !!(media && media.length);
  const headerHider = workoutObject.type === 'BR';

  const handlePlayVideo = () => {
    if (videoTrue && !headerHider && media[0]?.mediaUrl) {
      dispatch(setActiveModal('videoModal', { url: media[0].mediaUrl }));
    }
  };

  const buttonSnacker = (item) => (
    <>
      {item.optOutEnabled ? (
        <MiddleButton style={{ marginLeft: 0, marginRight: 5 }}>
          <IconTextWrapper>
            <IcomoonReact iconSet={iconSet} size={10} icon='opt-out' color='red' />
            <SubText fontSize='8px' fontWeight='700' letterSpacing='1px'>Opt Out</SubText>
          </IconTextWrapper>
        </MiddleButton>
      ) : null}
      {item.journalEntriesEnabled ? (
        <>
          <MiddleButton style={{ marginLeft: 5, marginRight: 5 }}>
            <IconTextWrapper>
              <IcomoonReact iconSet={iconSet} size={10} icon='journal' color='#0dcc8a' />
              <SubText fontSize='8px' fontWeight='700' letterSpacing='1px'>Journal</SubText>
            </IconTextWrapper>
            {item?.journalEntries?.length && item?.journalEntries[0]?.media?.length ? (
              <MediaCount>
                <IcomoonReact iconSet={iconSet} size={12} icon='video' color='white' />
                {item.journalEntries[0].media.length}
              </MediaCount>
            ) : null}
          </MiddleButton>
          <MiddleButton style={{ marginLeft: 5, marginRight: 0 }}>
            <IconTextWrapper>
              <IcomoonReact iconSet={iconSet} size={12} icon='video-add' color='rgb(184, 232, 249)' />
              <SubText fontSize='8px' fontWeight='700' letterSpacing='1px'>Media</SubText>
            </IconTextWrapper>
          </MiddleButton>
        </>
      ) : null}
    </>
  );

  const renderChopper = (workout) => {
    if (!workout) return null;
    if (workout.formType === 'Questions') return <CalendarQuestionnaire workoutObject={workout} />;
    if (workout.formType === 'TagSelect') return <CalendarTagCard workoutObject={workout} />;
    if (workout.formType === 'TextOnly') return <CalendarWorkoutNotes workoutObject={workout} />;
    if (workout.type === 'BR') return <CalendarSessionBreakCard workoutObject={workout} />;
    if (workout.type === 'H') return <CalendarHealthCard workoutObject={workout} />;
    if (workout.type === 'P') return <CalendarProgressionCard workoutObject={workout} />;
    if (workout.type === 'RT') return <CalendarTimerCard workoutObject={workout} />;
    if (workout.type === 'V') return <CalendarVideoCard workoutObject={workout} />;
    return workout?.tableHeaders ? <CalendarEntryTable workoutObject={workout} /> : null;
  };

  return (
    <Wrapper>
      <UpperBox isVideo={videoTrue} onClick={handlePlayVideo}>
        <VideoContainer>
          <UpperBoxBottom>
            <NameAndPlay>
              {videoTrue ? (
                <PlayButton>
                  <IcomoonReact iconSet={iconSet} size={15} icon='play-filled' color='white' />
                </PlayButton>
              ) : <div />}
              {!headerHider ? (
                <ExerciseName videoTrue={videoTrue}>{workoutObject.title}</ExerciseName>
              ) : null}
            </NameAndPlay>
            <MaxTempBox videoTrue={videoTrue}>
              {workoutObject.workingMax ? (
                <div>{`Max: ${workoutObject.workingMax.value}`}</div>
              ) : <div />}
              {workoutObject.tempo ? <div>{`Tempo: ${workoutObject.tempo}`}</div> : <div />}
            </MaxTempBox>
          </UpperBoxBottom>
          <VideoOverlay videoTrue={videoTrue} />
          <Video background={videoTrue ? media[0].thumbnailUrl : null} />
        </VideoContainer>
        <IconAndCardLine>
          <IconContainer
            borderColor={workoutObject?.groupingColorCode || 'grey'}
            completed={workoutObject?.fullyCompleted}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={15}
              icon={workoutObject.fullyCompleted ? 'checkmark' : workoutObject.icon}
              color={workoutObject.fullyCompleted ? 'white' : 'black'}
            />
            {workoutObject.groupingColorCode && workoutObject.groupingLetter ? (
              <GroupingLetterContainer
                borderColor={workoutObject?.groupingColorCode || 'black'}
                completed={workoutObject?.fullyCompleted}
              >
                <GroupingLetter color={theme.colors.background[0]}>
                  {workoutObject.groupingLetter}
                </GroupingLetter>
              </GroupingLetterContainer>
            ) : null}
          </IconContainer>
          {!workoutObject.fullyCompleted ? <CardLine /> : <GreenCardLine />}
        </IconAndCardLine>
      </UpperBox>
      <LowerBox>
        {showButtons(workoutObject) ? (
          <InteriorWrap>
            <MiddleButtons>{buttonSnacker(workoutObject)}</MiddleButtons>
            {workoutObject.optedOut ? (
              <OptOutWrapper>
                <OptOutText>
                  <strong>Opted out:</strong>
                  {' '}
                  {workoutObject.optedOutReason}
                </OptOutText>
                {workoutObject.substitutedExercise ? (
                  <OptOutText>
                    <strong>Substituted from:</strong>
                    {' '}
                    {workoutObject.substitutedExercise.name}
                  </OptOutText>
                ) : null}
              </OptOutWrapper>
            ) : null}
          </InteriorWrap>
        ) : null}
        {workoutObject?.documents?.length ? (
          <CalendarWorkoutDocuments workoutObject={workoutObject} />
        ) : null}
        {workoutObject.additionalInformation && workoutObject.formType !== 'Questions' ? (
          <AdditionalInformation>
            <AdditionalInformationText>
              {workoutObject.additionalInformation}
            </AdditionalInformationText>
          </AdditionalInformation>
        ) : null}
        {workoutObject.text && workoutObject.formType === 'Questions' ? (
          <AdditionalInformation>
            <AdditionalInformationText>{workoutObject.text}</AdditionalInformationText>
          </AdditionalInformation>
        ) : null}
        {renderChopper(workoutObject)}
        {workoutObject.additionalInformation && workoutObject.formType === 'Questions' ? (
          <AdditionalInformation>
            <Linkify componentDecorator={linkDecorator}>
              <AdditionalInformationText>
                {workoutObject.additionalInformation}
              </AdditionalInformationText>
            </Linkify>
          </AdditionalInformation>
        ) : null}
        <Overlay />
      </LowerBox>
    </Wrapper>
  );
};

CalendarExerciseDetail.propTypes = {
  workoutObject: PropTypes.instanceOf(Object).isRequired,
};

export default CalendarExerciseDetail;
